import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCombinedPremiumChart, fetchPremiumHistory } from '../api';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend, Area, ComposedChart, Line,
} from 'recharts';

const REFRESH_INTERVAL = 10000;

/* Custom tooltip matching the dark theme */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#161b22', border: '1px solid #30363d', borderRadius: 8,
      padding: '10px 14px', fontSize: 12, color: '#c9d1d9', lineHeight: 1.6,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <b>{typeof p.value === 'number' ? p.value.toFixed(2) : p.value}</b>
        </div>
      ))}
    </div>
  );
}

export default function PremiumChart() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [chartRes, histRes] = await Promise.all([
        fetchCombinedPremiumChart(),
        fetchPremiumHistory(),
      ]);
      if (chartRes.data?.status === 'ok') setData(chartRes.data);
      if (histRes.data?.status === 'ok') setHistory(histRes.data.series || {});
      setLastRefresh(new Date().toLocaleTimeString());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    const id = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [loadData]);

  if (loading && !data) {
    return <div className="loading"><div className="spinner" /><p>Loading premium data...</p></div>;
  }

  const accounts = data?.accounts || [];

  if (accounts.length === 0) {
    return (
      <div className="page-premium-chart">
        <div className="pc-header">
          <h1 className="pc-title">Positions</h1>
        </div>
        <div className="hub-tabs">
          <button className="hub-tab" onClick={() => navigate('/positions')}>Position Book</button>
          <button className="hub-tab hub-tab-active">Premium Charts</button>
          <button className="hub-tab" onClick={() => navigate('/option-chain')}>Option Chain</button>
        </div>
        <div className="dash-empty">No open sell positions found across any account</div>
      </div>
    );
  }

  return (
    <div className="page-premium-chart" style={{ maxWidth: 1400 }}>
      <div className="pc-header">
        <h1 className="pc-title">Positions</h1>
        <span className="live-indicator">
          <span className="live-pulse" /> Live · {lastRefresh}
        </span>
      </div>

      <div className="hub-tabs">
        <button className="hub-tab" onClick={() => navigate('/positions')}>Position Book</button>
        <button className="hub-tab hub-tab-active">Premium Charts</button>
        <button className="hub-tab" onClick={() => navigate('/option-chain')}>Option Chain</button>
      </div>

      {accounts.map((acct) => (
        <div key={acct.account_name} className="pc-account-card">
          <div className="pc-acct-header">
            <div className="pc-acct-name">{acct.account_name}</div>
          </div>

          {(acct.indices || []).map((idx) => {
            const histKey = `${acct.account_name}|${idx.index}`;
            const series = history[histKey]?.data || [];

            return (
              <div key={idx.index} className="pc-index-section">
                {/* Index Header */}
                <div className="pc-index-header">
                  <div className="pc-index-name">{idx.index}</div>
                  <div className={`pc-acct-pnl ${idx.total_pnl_points >= 0 ? 'positive' : 'negative'}`}>
                    {idx.total_pnl_points >= 0 ? '+' : ''}{idx.total_pnl_points.toFixed(2)} pts
                    <span className="pc-acct-pnl-amt">
                      {' '}({idx.total_pnl_amount >= 0 ? '+' : '-'}₹{Math.abs(idx.total_pnl_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })})
                    </span>
                  </div>
                </div>

                {/* === Combined Premium Line Chart === */}
                <div className="pc-chart-box">
                  <div className="pc-chart-title">Combined Premium</div>
                  {series.length < 2 ? (
                    <div className="pc-chart-waiting">
                      Collecting data... ({series.length} point{series.length !== 1 ? 's' : ''})
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <ComposedChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id={`grad-${histKey}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#58a6ff" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#58a6ff" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                        <XAxis dataKey="time" tick={{ fill: '#484f58', fontSize: 11 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fill: '#484f58', fontSize: 11 }} domain={['auto', 'auto']} />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 12, color: '#8b949e' }} />
                        <ReferenceLine
                          y={idx.total_combined_sell}
                          stroke="#f0883e"
                          strokeDasharray="6 3"
                          strokeWidth={2}
                          label={{ value: `Entry ${idx.total_combined_sell.toFixed(1)}`, fill: '#f0883e', fontSize: 11, position: 'right' }}
                        />
                        <Area
                          type="monotone" dataKey="combined" name="Combined"
                          stroke="#58a6ff" fill={`url(#grad-${histKey})`}
                          strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* === CE vs PE Line Chart === */}
                <div className="pc-chart-box">
                  <div className="pc-chart-title">CE vs PE Premium</div>
                  {series.length < 2 ? (
                    <div className="pc-chart-waiting">Collecting data...</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <ComposedChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                        <XAxis dataKey="time" tick={{ fill: '#484f58', fontSize: 11 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fill: '#484f58', fontSize: 11 }} domain={['auto', 'auto']} />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 12, color: '#8b949e' }} />
                        <Line type="monotone" dataKey="ce" name="CE Premium" stroke="#56d364" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                        <Line type="monotone" dataKey="pe" name="PE Premium" stroke="#f85149" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* === P&L Line Chart === */}
                <div className="pc-chart-box">
                  <div className="pc-chart-title">P&L (points)</div>
                  {series.length < 2 ? (
                    <div className="pc-chart-waiting">Collecting data...</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <ComposedChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id={`pnlGrad-${histKey}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#56d364" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#56d364" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
                        <XAxis dataKey="time" tick={{ fill: '#484f58', fontSize: 11 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fill: '#484f58', fontSize: 11 }} domain={['auto', 'auto']} />
                        <Tooltip content={<ChartTooltip />} />
                        <ReferenceLine y={0} stroke="#484f58" strokeWidth={1} />
                        <Area
                          type="monotone" dataKey="pnl" name="P&L"
                          stroke="#56d364" fill={`url(#pnlGrad-${histKey})`}
                          strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Summary cards below charts */}
                <div className="pc-summary-row">
                  <div className="pc-summary-card">
                    <span className="pc-summary-label">Entry Premium</span>
                    <span className="pc-summary-val">{idx.total_combined_sell.toFixed(2)}</span>
                  </div>
                  <div className="pc-summary-card">
                    <span className="pc-summary-label">Current Premium</span>
                    <span className={`pc-summary-val ${idx.total_combined_current <= idx.total_combined_sell ? 'positive' : 'negative'}`}>
                      {idx.total_combined_current.toFixed(2)}
                    </span>
                  </div>
                  <div className="pc-summary-card" style={{ borderLeft: '3px solid #56d364' }}>
                    <span className="pc-summary-label">CE</span>
                    <span className="pc-summary-val">{idx.total_ce_current.toFixed(2)}</span>
                    <span className={`pc-summary-sub ${idx.total_ce_sell - idx.total_ce_current >= 0 ? 'positive' : 'negative'}`}>
                      {(idx.total_ce_sell - idx.total_ce_current) >= 0 ? '+' : ''}{(idx.total_ce_sell - idx.total_ce_current).toFixed(2)}
                    </span>
                  </div>
                  <div className="pc-summary-card" style={{ borderLeft: '3px solid #f85149' }}>
                    <span className="pc-summary-label">PE</span>
                    <span className="pc-summary-val">{idx.total_pe_current.toFixed(2)}</span>
                    <span className={`pc-summary-sub ${idx.total_pe_sell - idx.total_pe_current >= 0 ? 'positive' : 'negative'}`}>
                      {(idx.total_pe_sell - idx.total_pe_current) >= 0 ? '+' : ''}{(idx.total_pe_sell - idx.total_pe_current).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Per-Strategy Table */}
                <div className="pc-strategies">
                  <div className="pc-strat-header-row">
                    <span className="pc-sh-name">Strategy</span>
                    <span className="pc-sh-strike">CE Strike</span>
                    <span className="pc-sh-strike">PE Strike</span>
                    <span className="pc-sh-prem">CE Sell</span>
                    <span className="pc-sh-prem">PE Sell</span>
                    <span className="pc-sh-prem">CE Now</span>
                    <span className="pc-sh-prem">PE Now</span>
                    <span className="pc-sh-prem">Combined</span>
                    <span className="pc-sh-pnl">P&L</span>
                  </div>
                  {idx.strategies.map((s) => (
                    <div key={s.name} className="pc-strat-row">
                      <div className="pc-strat-info">
                        <span className="pc-strat-name">{s.name}</span>
                        <span className="pc-strat-meta">{s.lots}L × {s.lot_size}</span>
                      </div>
                      <span className="pc-strat-strike">{typeof s.ce_strike === 'number' ? s.ce_strike.toLocaleString() : s.ce_strike}</span>
                      <span className="pc-strat-strike">{typeof s.pe_strike === 'number' ? s.pe_strike.toLocaleString() : s.pe_strike}</span>
                      <span className="pc-strat-prem">{s.ce_sell_price.toFixed(1)}</span>
                      <span className="pc-strat-prem">{s.pe_sell_price.toFixed(1)}</span>
                      <span className="pc-strat-prem">{s.ce_current_price.toFixed(1)}</span>
                      <span className="pc-strat-prem">{s.pe_current_price.toFixed(1)}</span>
                      <span className="pc-strat-prem">{s.combined_sell.toFixed(1)} → {s.combined_current.toFixed(1)}</span>
                      <span className={`pc-strat-pnl ${s.pnl_points >= 0 ? 'positive' : 'negative'}`}>
                        {s.pnl_points >= 0 ? '+' : ''}{s.pnl_points.toFixed(1)}
                        <span className="pc-strat-pnl-amt">
                          {s.pnl_amount >= 0 ? '+' : '-'}₹{Math.abs(s.pnl_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
